import { getBackendUrl } from '../utils/api';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import storage from '../utils/storage';

const BACKEND_URL = getBackendUrl() || '';

const SubscriptionAlert = () => {
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const token = storage.getItem('token');
      const user = storage.getItem('user');
      
      if (!token || !user) {
        setLoading(false);
        return;
      }

      // Parse user data to get username
      const userData = typeof user === 'string' ? JSON.parse(user) : user;
      const username = userData.username;

      if (!username) {
        setLoading(false);
        return;
      }

      const response = await axios.get(`${BACKEND_URL}/api/subscription/status?username=${username}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !subscriptionStatus) {
    return null;
  }

  // Don't show alert if subscription is not active or doesn't need renewal
  if (subscriptionStatus.status !== 'active' || !subscriptionStatus.needs_renewal) {
    return null;
  }

  const { days_remaining, urgent, expires_at } = subscriptionStatus;

  // Format expiry date
  const expiryDate = new Date(expires_at);
  const formattedDate = expiryDate.toLocaleDateString('el-GR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });

  // Determine alert style based on urgency
  const alertStyles = urgent
    ? {
        bg: 'bg-red-50',
        border: 'border-red-500',
        text: 'text-red-900',
        icon: '🚨',
        buttonBg: 'bg-red-600 hover:bg-red-700'
      }
    : days_remaining <= 3
    ? {
        bg: 'bg-orange-50',
        border: 'border-orange-500',
        text: 'text-orange-900',
        icon: '⚠️',
        buttonBg: 'bg-orange-600 hover:bg-orange-700'
      }
    : {
        bg: 'bg-blue-50',
        border: 'border-blue-500',
        text: 'text-blue-900',
        icon: '🔔',
        buttonBg: 'bg-blue-600 hover:bg-blue-700'
      };

  const handleRenew = async () => {
    try {
      const token = storage.getItem('token');
      if (!token) {
        alert('Παρακαλώ κάντε login για να ανανεώσετε τη συνδρομή');
        return;
      }

      // Create checkout session for subscription renewal
      const response = await axios.post(
        `${BACKEND_URL}/api/checkout/create`,
        {
          package: 'initial', // Regular 30-day subscription
          origin_url: window.location.origin,
          auto_recharge: false
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Redirect to Stripe checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        alert('Σφάλμα δημιουργίας checkout. Παρακαλώ δοκιμάστε ξανά.');
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      alert('Σφάλμα: ' + (error.response?.data?.detail || 'Παρακαλώ δοκιμάστε ξανά'));
    }
  };

  return (
    <div className={`${alertStyles.bg} border-l-4 ${alertStyles.border} p-4 mb-6 rounded-lg shadow-md`}>
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 text-2xl mt-0.5">
          {alertStyles.icon}
        </div>
        <div className="flex-1">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className={`text-lg font-bold ${alertStyles.text}`}>
                {urgent
                  ? days_remaining === 0
                    ? 'Η συνδρομή σου λήγει σήμερα!'
                    : days_remaining === 1
                    ? 'Η συνδρομή σου λήγει αύριο!'
                    : `Η συνδρομή σου λήγει σε ${days_remaining} ημέρες!`
                  : `Υπενθύμιση: Η συνδρομή σου λήγει σε ${days_remaining} ημέρες`}
              </h3>
              <p className={`text-sm ${alertStyles.text} mt-1`}>
                <strong>Ημερομηνία λήξης:</strong> {formattedDate}
              </p>
              {urgent && (
                <p className={`text-sm ${alertStyles.text} mt-2 font-medium`}>
                  ⚡ Ανανέωσε τώρα για να αποφύγεις διακοπή της υπηρεσίας!
                </p>
              )}
            </div>
            <button
              onClick={handleRenew}
              className={`${alertStyles.buttonBg} text-white px-6 py-2 rounded-lg font-medium transition flex-shrink-0`}
            >
              🔄 Ανανέωση Συνδρομής
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionAlert;
